import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  RegistrationPending,
  TokenPair,
  UserPublic,
} from '@osd/shared-types';
import { api, ApiError, setUnauthorizedHandler } from '../../lib/api';

/**
 * Client-side auth state + token storage — `features/auth` slice.
 *
 * Tokens are kept in `localStorage` (keys: `osd.auth.access`,
 * `osd.auth.refresh`). This is intentional for now — the OSD app
 * is small, the threat model is "not a bank", and the alternative
 * (HttpOnly cookies) requires CORS + CSRF coordination that isn't
 * on the table right now.
 *
 * Note: the current `/api/auth/register` endpoint returns 202 +
 * pending (no tokens), so `register()` will fall through with a
 * confused `setUser(undefined)` until the API contract is honoured
 * on both sides. Filed as a follow-up.
 */

const ACCESS_KEY = 'osd.auth.access';
const REFRESH_KEY = 'osd.auth.refresh';
const IS_ADMIN_KEY = 'osd.auth.isAdmin';

function writeIsAdmin(isAdmin: boolean | undefined): void {
  if (isAdmin === true) {
    localStorage.setItem(IS_ADMIN_KEY, 'true');
  } else {
    localStorage.removeItem(IS_ADMIN_KEY);
  }
}

function readTokens(): TokenPair | null {
  const accessToken = localStorage.getItem(ACCESS_KEY);
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!accessToken || !refreshToken) return null;
  // Decode the `exp` claim from the access JWT (HS256 payload, base64url).
  // We previously hand-constructed `expiresAt = ''` here, which violated
  // the Zod schema (`z.string().datetime()`) and shipped a dead field
  // through every TokenPair reconstruction. Now we recover the real
  // server-issued expiry from the token itself — no extra storage needed
  // and the field is meaningful again. Server-side JWT verification
  // (jwt.ts) remains the source of truth; this is informational. (root/osd#95)
  const expiresAt = jwtExpiry(accessToken) ?? '';
  return {
    accessToken,
    refreshToken,
    expiresAt,
    tokenType: 'Bearer',
  };
}

/**
 * Decode the `exp` claim from a base64url-encoded HS256 JWT payload.
 * Returns an ISO-8601 datetime string, or `null` if the token is
 * malformed / has no exp claim. No signature verification — the
 * server is the source of truth for token validity.
 */
function jwtExpiry(accessToken: string): string | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;
    const payloadB64 = parts[1];
    if (payloadB64 === undefined) return null;
    // base64url → base64 → atob
    const b64 = payloadB64.replaceAll('-', '+').replaceAll('_', '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const claims = JSON.parse(json) as { exp?: unknown };
    if (typeof claims.exp !== 'number') return null;
    return new Date(claims.exp * 1000).toISOString();
  } catch {
    return null;
  }
}

function writeTokens(tokens: TokenPair | null): void {
  if (tokens === null) {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    return;
  }
  localStorage.setItem(ACCESS_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
}

export interface AuthState {
  /** Current user, or `null` if not signed in. */
  readonly user: UserPublic | null;
  /** True while the initial `/api/auth/me` probe is in flight. */
  readonly loading: boolean;
  /** Last error from a login attempt (consumed by LoginForm). */
  readonly error: string | null;
}

export interface AuthActions {
  readonly login: (values: LoginRequest) => Promise<void>;
  /**
   * Register a new user. Distinguishes the two API outcomes:
   * - `AuthResponse` — tokens issued (defensive — the current API is
   *   always 202 + pending under the default-deny invite flow)
   * - `RegistrationPending` — 202; the account is parked and an admin
   *   must approve before sign-in.
   * Throws on 4xx/5xx; auth.error is also set on 409.
   */
  readonly register: (values: RegisterRequest) => Promise<AuthResponse | RegistrationPending>;
  readonly logout: () => Promise<void>;
  readonly clearError: () => void;
}

export type AuthContextValue = AuthState & AuthActions;

function describeError(
  err: unknown,
  statusCode: number,
  forStatus: string,
  fallback: string,
): string {
  if (err instanceof ApiError && err.status === statusCode) return forStatus;
  if (err instanceof Error) return err.message;
  return fallback;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * AuthProvider — single source of truth for the current user.
 *
 * Mounted once at the app root. On mount, reads tokens from
 * `localStorage` and probes `/api/auth/me`. If the probe fails,
 * tokens are cleared and `user` stays `null`.
 */
export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to the api layer's "refresh definitively failed" signal.
  // When fired, clear local tokens + user state so the UI flips to
  // "logged out" and the user re-logs in. (root/osd#2)
  useEffect(() => {
    return setUnauthorizedHandler(() => {
      writeTokens(null);
      writeIsAdmin(false);
      setUser(null);
    });
  }, []);

  // Probe /api/auth/me on mount to validate any persisted tokens.
  useEffect(() => {
    let cancelled = false;
    const tokens = readTokens();
    if (tokens === null) {
      setLoading(false);
      return;
    }
    void api
      .auth()
      .me()
      .then((res) => {
        if (cancelled) return;
        setUser(res.user);
        // Mirror the user's isAdmin flag into localStorage so the
        // nav + admin route guards can read it synchronously
        // (avoiding a flash of "non-admin nav" before /me
        // resolves). The flag is the SOURCE OF TRUTH SOURCE OF
        // HINT — every admin route re-checks via /me via the api
        // layer on every request, so a stale flag can only ever
        // surface a redirect-to-/ (non-admin landing), not a
        // privilege escalation.
        writeIsAdmin(res.user.isAdmin);
      })
      .catch(() => {
        if (cancelled) return;
        writeTokens(null);
        writeIsAdmin(false);
        setUser(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (values: LoginRequest): Promise<void> => {
    setError(null);
    try {
      const res: AuthResponse = await api.auth().login(values);
      writeTokens(res.tokens);
      writeIsAdmin(res.user.isAdmin);
      setUser(res.user);
    } catch (err) {
      const msg = describeError(err, 401, 'Invalid email or password.', 'Sign-in failed.');
      setError(msg);
      throw err;
    }
  }, []);

  const register = useCallback(
    async (values: RegisterRequest): Promise<AuthResponse | RegistrationPending> => {
      setError(null);
      try {
        const res: AuthResponse | RegistrationPending = await api.auth().register(values);
        // /api/auth/register returns 202 + pending (no tokens) under
        // the default-deny approval workflow (root/osd#22). If tokens
        // are present (e.g. future invite-token mode, root/osd#22
        // Phase 2), persist them + flip to logged in. The route
        // (/register) reads the returned shape to decide which UI to
        // show — "pending approval" vs. immediate redirect to '/'.
        if ('tokens' in res && res.tokens) {
          writeTokens(res.tokens);
          writeIsAdmin(res.user.isAdmin);
          setUser(res.user);
        }
        return res;
      } catch (err) {
        const msg = describeError(
          err,
          409,
          'Could not create account with those credentials.',
          'Registration failed.',
        );
        setError(msg);
        throw err;
      }
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await api.auth().logout();
    } catch {
      // Logout is best-effort — always clear local state even if the
      // server call fails (e.g. token already revoked).
    }
    writeTokens(null);
    writeIsAdmin(false);
    setUser(null);
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, login, register, logout, clearError }),
    [user, loading, error, login, register, logout, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** useAuth — read the auth state + actions from the React tree. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be called inside <AuthProvider>');
  }
  return ctx;
}
